// Herkomst van een bezoeker: via welk kanaal en op welke pagina kwam hij binnen.
// De calculator stuurt dit mee met de lead, zodat HQ organisch Google, AI-assistenten en
// rechtstreeks bezoek van elkaar onderscheidt (anders is elke lead zonder UTM gewoon "Website").
// Contract met marketing/funnels/calculator: sessionStorage "wrappie:herkomst:v1" =
// { source, medium, campaign?, content?, term?, gclid?, fbclid?, landing, at }. Regel: de laatste niet-rechtstreekse bron wint (zoals GA4);
// rechtstreeks bezoek overschrijft nooit een eerder gekend kanaal. Alleen voor dit bezoek
// (sessionStorage): niets blijft in de browser staan, dus geen cookietoestemming nodig.

export const HERKOMST_KEY = "wrappie:herkomst:v1";
const UTM = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;

const KLIK_IDS = ["gclid", "fbclid"] as const;
const CAMPAGNE_PARAMS = [...UTM, ...KLIK_IDS] as const;

export type Herkomst = {
  source: string;
  medium: string;
  campaign?: string;
  content?: string;
  term?: string;
  gclid?: string;
  fbclid?: string;
  landing: string;
  at: number;
};

/** Wat HQ bij een lead als herkomst bewaart (zelfde velden als de calculator-lead). */
export type LeadAttributie = {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  gclid?: string;
  fbclid?: string;
  landingPage: string;
};

const AI = ["chatgpt.com", "chat.openai.com", "perplexity.ai", "gemini.google.com", "copilot.microsoft.com", "claude.ai"];
const ZOEKMACHINES: [RegExp, string][] = [
  [/(^|\.)google\.[a-z.]+$/, "google"],
  [/(^|\.)bing\.com$/, "bing"],
  [/(^|\.)duckduckgo\.com$/, "duckduckgo"],
  [/(^|\.)ecosia\.org$/, "ecosia"],
  [/(^|\.)yahoo\.com$/, "yahoo"],
  [/(^|\.)qwant\.com$/, "qwant"],
];
const SOCIAAL: [RegExp, string][] = [
  [/(^|\.)facebook\.com$/, "facebook"],
  [/(^|\.)instagram\.com$/, "instagram"],
  [/(^|\.)linkedin\.com$/, "linkedin"],
  [/(^|\.)pinterest\.[a-z.]+$/, "pinterest"],
  [/(^|\.)tiktok\.com$/, "tiktok"],
  [/(^|\.)youtube\.com$/, "youtube"],
];

/** Bepaalt het kanaal van dit paginabezoek; null = rechtstreeks of intern (niets nieuws). */
export function kanaal(url: URL, referrer: string): { source: string; medium: string } | null {
  const q = url.searchParams;
  const utm = q.get("utm_source");
  // ChatGPT en Perplexity zetten zelf utm_source=<host> zonder medium.
  if (utm) return { source: utm, medium: q.get("utm_medium") || (AI.includes(utm) ? "ai" : "") };
  if (q.get("gclid")) return { source: "google", medium: "cpc" };
  if (q.get("fbclid")) return { source: "facebook", medium: "social" };
  let host: string;
  try {
    host = new URL(referrer).hostname.toLowerCase().replace(/^www\./, "");
  } catch {
    return null;
  }
  if (!host || host === url.hostname.replace(/^www\./, "") || host.endsWith(".wrappie.be") || host === "wrappie.be") return null;
  if (AI.some((ai) => host === ai || host.endsWith(`.${ai}`))) return { source: host, medium: "ai" };
  for (const [re, naam] of ZOEKMACHINES) if (re.test(host)) return { source: naam, medium: "organic" };
  for (const [re, naam] of SOCIAAL) if (re.test(host)) return { source: naam, medium: "social" };
  return { source: host, medium: "referral" };
}

// Campagne, advertentie, zoekwoord en klik-ID's uit de URL; alleen wat er echt staat.
function campagne(url: URL): Partial<Herkomst> {
  const q = url.searchParams;
  const velden: [keyof Herkomst, string | null][] = [
    ["campaign", q.get("utm_campaign")],
    ["content", q.get("utm_content")],
    ["term", q.get("utm_term")],
    ...KLIK_IDS.map((id) => [id, q.get(id)] as [keyof Herkomst, string | null]),
  ];
  return Object.fromEntries(velden.filter(([, waarde]) => waarde).map(([veld, waarde]) => [veld, waarde!.slice(0, 500)]));
}

/** Nieuwe opgeslagen herkomst na dit bezoek; null = de bestaande blijft staan. */
export function volgende(vorige: Herkomst | null, url: URL, referrer: string, nu: number): Herkomst | null {
  const nieuw = kanaal(url, referrer);
  // Alleen campagneparameters blijven in de instappagina: andere querystring kan persoonsgegevens bevatten.
  const query = url.search.slice(1).split("&")
    .filter((deel) => (CAMPAGNE_PARAMS as readonly string[]).includes(deel.split("=")[0]))
    .join("&");
  const landing = url.origin + url.pathname + (query ? `?${query}` : "");
  if (nieuw) return { ...nieuw, ...campagne(url), landing, at: nu };
  if (!vorige) return { source: "direct", medium: "none", landing, at: nu };
  return null;
}

export function bewaarHerkomst(storage: Storage, url: URL, referrer: string, nu = Date.now()) {
  try {
    const raw = storage.getItem(HERKOMST_KEY);
    const vorige = raw ? (JSON.parse(raw) as Herkomst) : null;
    const nieuw = volgende(vorige && typeof vorige.at === "number" ? vorige : null, url, referrer, nu);
    if (nieuw) storage.setItem(HERKOMST_KEY, JSON.stringify(nieuw));
  } catch {
    // Geblokkeerde opslag: de lead valt dan terug op de URL zelf.
  }
}

/** Herkomst voor een lead: de bewaarde herkomst van dit bezoek, anders de huidige pagina zelf. */
export function leadAttributie(storage: Storage, url: URL): LeadAttributie {
  let herkomst: Herkomst | null = null;
  try {
    const raw = storage.getItem(HERKOMST_KEY);
    const waarde = raw ? (JSON.parse(raw) as Herkomst) : null;
    herkomst = waarde && typeof waarde.source === "string" && typeof waarde.landing === "string" ? waarde : null;
  } catch {
    // Geblokkeerde of kapotte opslag: terugvallen op de URL.
  }
  herkomst ??= volgende(null, url, "", Date.now());
  if (!herkomst) return { landingPage: url.origin + url.pathname };
  const velden: [keyof LeadAttributie, string | undefined][] = herkomst.source === "direct" ? [] : [
    ["utmSource", herkomst.source],
    ["utmMedium", herkomst.medium],
    ["utmCampaign", herkomst.campaign],
    ["utmContent", herkomst.content],
    ["utmTerm", herkomst.term],
    ["gclid", herkomst.gclid],
    ["fbclid", herkomst.fbclid],
  ];
  return { ...Object.fromEntries(velden.filter(([, waarde]) => waarde)), landingPage: herkomst.landing } as LeadAttributie;
}
